import React, { useState, useEffect } from 'react'
import Icon from '../views/components/Icon'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from '@tanstack/react-table'

interface PaginationData {
  currentPage: number
  totalPages: number
  totalItems: number
  itemsPerPage: number
}

interface DataTableProps<T> {
  data: T[]
  columns: ColumnDef<T>[]
  searchPlaceholder?: string
  emptyStateTitle?: string
  emptyStateDescription?: string
  emptyStateIcon?: React.ReactNode
  onCreateClick?: () => void
  createButtonText?: string
  showCreateButton?: boolean
  loading?: boolean
  onRowClick?: (row: T) => void
  // Server-side pagination props
  pagination?: PaginationData
  onPageChange?: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
  onSearchChange?: (search: string) => void
  serverSide?: boolean
}

function DataTable<T>({
  data,
  columns,
  searchPlaceholder = 'Search...',
  emptyStateTitle = 'No items',
  emptyStateDescription = 'Get started by creating your first item.',
  emptyStateIcon,
  onCreateClick,
  createButtonText = 'Create Item',
  showCreateButton = true,
  loading = false,
  onRowClick,
  pagination: serverPagination,
  onPageChange,
  onPageSizeChange,
  onSearchChange,
  serverSide = false,
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [isMobile, setIsMobile] = useState(false)

  // Check for mobile screen size
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)

    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  // Handle search with debouncing for server-side
  useEffect(() => {
    if (serverSide && onSearchChange) {
      const timeoutId = setTimeout(() => {
        onSearchChange(globalFilter)
      }, 300) // 300ms debounce

      return () => clearTimeout(timeoutId)
    }
  }, [globalFilter, serverSide, onSearchChange])

  // Create table instance
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // Only use client-side pagination if not server-side
    ...(serverSide ? {} : { getPaginationRowModel: getPaginationRowModel() }),
    initialState: {
      pagination: {
        pageSize: serverPagination?.itemsPerPage || 10,
      },
    },
    // Disable client-side pagination for server-side
    manualPagination: serverSide,
    pageCount: serverSide ? serverPagination?.totalPages || 1 : undefined,
  })

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
      {data.length === 0 ? (
        <div className="text-center py-12">
          {emptyStateIcon || (
            <Icon type="building" className="mx-auto h-12 w-12 text-gray-400" />
          )}
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {emptyStateTitle}
          </h3>
          <p className="mt-1 text-sm text-gray-500">{emptyStateDescription}</p>
          {showCreateButton && onCreateClick && (
            <div className="mt-6">
              <button
                onClick={onCreateClick}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                {createButtonText}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Search/Filter Bar */}
          <div className="px-4 md:px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1 max-w-lg">
                <input
                  value={globalFilter ?? ''}
                  onChange={(e) => setGlobalFilter(String(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={searchPlaceholder}
                />
              </div>
              <div className="text-sm text-gray-700 text-center sm:text-right">
                {serverSide
                  ? `${serverPagination?.totalItems || 0} total items`
                  : `${table.getFilteredRowModel().rows.length} of ${data.length} items`}
              </div>
            </div>
          </div>

          {/* Mobile Card Layout */}
          {isMobile ? (
            <div className="px-4 space-y-3">
              {table.getRowModel().rows.map((row) => (
                <div
                  key={row.id}
                  className={`bg-white border border-gray-200 rounded-lg p-4 ${
                    onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''
                  }`}
                  onClick={() => onRowClick && onRowClick(row.original)}
                >
                  {row.getVisibleCells().map((cell) => {
                    const columnDef = cell.column.columnDef
                    const header =
                      typeof columnDef.header === 'string'
                        ? columnDef.header
                        : String(columnDef.header)

                    // Skip actions column in card view - we'll handle it separately
                    if (cell.column.id === 'actions') return null

                    return (
                      <div
                        key={cell.id}
                        className="flex justify-between items-start py-1 border-b border-gray-100 last:border-b-0"
                      >
                        <span className="text-sm font-medium text-gray-500 min-w-0 flex-shrink-0 mr-4">
                          {header}:
                        </span>
                        <div className="text-sm text-gray-900 text-right flex-1 min-w-0">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </div>
                      </div>
                    )
                  })}

                  {/* Actions at bottom of card */}
                  {row
                    .getVisibleCells()
                    .find((cell) => cell.column.id === 'actions') && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      {flexRender(
                        row
                          .getVisibleCells()
                          .find((cell) => cell.column.id === 'actions')!.column
                          .columnDef.cell,
                        row
                          .getVisibleCells()
                          .find((cell) => cell.column.id === 'actions')!
                          .getContext(),
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            /* Desktop Table Layout */
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <div className="flex items-center gap-2">
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                            <span className="text-gray-400">
                              {{
                                asc: ' 🔼',
                                desc: ' 🔽',
                              }[header.column.getIsSorted() as string] ?? ''}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className={`hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''}`}
                      onDoubleClick={() =>
                        onRowClick && onRowClick(row.original)
                      }
                    >
                      {row.getVisibleCells().map((cell) => {
                        const cellValue = cell.getValue()
                        const tooltipText =
                          typeof cellValue === 'string' ||
                          typeof cellValue === 'number'
                            ? String(cellValue)
                            : undefined

                        return (
                          <td key={cell.id} className="px-6 py-4">
                            <div
                              className="truncate max-w-sm"
                              title={tooltipText}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </div>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="px-4 md:px-6 py-3 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {/* Navigation Buttons */}
              <div className="flex items-center gap-2 justify-center sm:justify-start">
                <button
                  className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  onClick={() =>
                    serverSide ? onPageChange?.(1) : table.setPageIndex(0)
                  }
                  disabled={
                    serverSide
                      ? serverPagination?.currentPage === 1
                      : !table.getCanPreviousPage()
                  }
                >
                  {'<<'}
                </button>
                <button
                  className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  onClick={() =>
                    serverSide
                      ? onPageChange?.((serverPagination?.currentPage || 1) - 1)
                      : table.previousPage()
                  }
                  disabled={
                    serverSide
                      ? serverPagination?.currentPage === 1
                      : !table.getCanPreviousPage()
                  }
                >
                  {'<'}
                </button>
                <button
                  className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  onClick={() =>
                    serverSide
                      ? onPageChange?.((serverPagination?.currentPage || 1) + 1)
                      : table.nextPage()
                  }
                  disabled={
                    serverSide
                      ? serverPagination?.currentPage ===
                        serverPagination?.totalPages
                      : !table.getCanNextPage()
                  }
                >
                  {'>'}
                </button>
                <button
                  className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  onClick={() =>
                    serverSide
                      ? onPageChange?.(serverPagination?.totalPages || 1)
                      : table.setPageIndex(table.getPageCount() - 1)
                  }
                  disabled={
                    serverSide
                      ? serverPagination?.currentPage ===
                        serverPagination?.totalPages
                      : !table.getCanNextPage()
                  }
                >
                  {'>>'}
                </button>
              </div>

              {/* Page Info */}
              <div className="flex items-center gap-2 text-sm text-gray-700 justify-center">
                <span>Page</span>
                <strong>
                  {serverSide
                    ? serverPagination?.currentPage
                    : table.getState().pagination.pageIndex + 1}{' '}
                  of{' '}
                  {serverSide
                    ? serverPagination?.totalPages
                    : table.getPageCount()}
                </strong>
              </div>

              {/* Page Controls */}
              <div className="flex flex-col sm:flex-row items-center gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Go to:</span>
                  <input
                    type="number"
                    defaultValue={
                      serverSide
                        ? serverPagination?.currentPage
                        : table.getState().pagination.pageIndex + 1
                    }
                    onChange={(e) => {
                      const page = e.target.value ? Number(e.target.value) : 1
                      if (serverSide) {
                        onPageChange?.(page)
                      } else {
                        table.setPageIndex(page - 1)
                      }
                    }}
                    className="w-16 px-2 py-1 border border-gray-300 rounded text-center"
                  />
                </div>

                <select
                  value={
                    serverSide
                      ? serverPagination?.itemsPerPage
                      : table.getState().pagination.pageSize
                  }
                  onChange={(e) => {
                    const pageSize = Number(e.target.value)
                    if (serverSide) {
                      onPageSizeChange?.(pageSize)
                    } else {
                      table.setPageSize(pageSize)
                    }
                  }}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  {[10, 20, 30, 40, 50].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      Show {pageSize}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DataTable
